import React, { useState } from "react";
import moment from "moment";
import Select from "./Select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FunnelFill, CalendarIcon } from "../../assets/img/icons";

/**
 * CustomDateFilter Component
 * Provides a dropdown with predefined date ranges and a custom range option.
 *
 * @param {Function} onRangeChange - Callback called with { start, end } as strings
 * @param {string} initialStartDate - YYYY-MM-DD
 * @param {string} initialEndDate - YYYY-MM-DD
 * @param {string} defaultFilter - Default preset (e.g., 'last_week')
 */
const CustomDateFilter = ({
  onRangeChange,
  initialStartDate,
  initialEndDate,
  defaultFilter = "today"
}) => {
  const [dateFilter, setDateFilter] = useState(defaultFilter);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const dateOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "this_week" },
    { label: "Last Week", value: "last_week" },
    { label: "This Month", value: "this_month" },
    { label: "Last Month", value: "last_month" },
    { label: "Custom Date", value: "custom" }
  ];

  const handleDateFilterChange = selected => {
    const value = selected ? selected.value : defaultFilter;
    setDateFilter(value);

    if (value === "custom") return;

    let start = moment();
    let end = moment();

    switch (value) {
      case "today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "yesterday":
        start = moment()
          .subtract(1, "days")
          .startOf("day");
        end = moment()
          .subtract(1, "days")
          .endOf("day");
        break;
      case "this_week":
        start = moment().startOf("week");
        end = moment().endOf("week");
        break;
      case "last_week":
        start = moment()
          .subtract(1, "weeks")
          .startOf("week");
        end = moment()
          .subtract(1, "weeks")
          .endOf("week");
        break;
      case "this_month":
        start = moment().startOf("month");
        end = moment().endOf("month");
        break;
      case "last_month":
        start = moment()
          .subtract(1, "months")
          .startOf("month");
        end = moment()
          .subtract(1, "months")
          .endOf("month");
        break;
      default:
        break;
    }

    const startStr = start.format("YYYY-MM-DD");
    const endStr = end.format("YYYY-MM-DD");

    setStartDate(startStr);
    setEndDate(endStr);

    if (onRangeChange) {
      onRangeChange({ start: startStr, end: endStr });
    }
  };

  const handleCustomDateChange = (type, date) => {
    const dateStr = moment(date).format("YYYY-MM-DD");
    let newStart = startDate;
    let newEnd = endDate;

    if (type === "start") {
      newStart = dateStr;
      setStartDate(dateStr);
    } else {
      newEnd = dateStr;
      setEndDate(dateStr);
    }

    if (onRangeChange) {
      onRangeChange({ start: newStart, end: newEnd });
    }
  };

  return (
    <div className="date-premium-filter-layout d-flex align-items-center mb-4 p-3 ring-0 shadow-sm">
      <div className="filter-side-badge d-flex align-items-center">
        <div className="icon-badge">
          <FunnelFill />
        </div>
        <div className="badge-text ml-2">
          <span className="label">Timeline</span>
          <span className="value">
            {(() => {
              const selected = dateOptions.find(o => o.value === dateFilter);
              return selected ? selected.label : "";
            })()}
          </span>
        </div>
      </div>

      <div className="filter-controls-area flex-grow-1">
        <div
          className="d-flex flex-column"
          style={{ gap: dateFilter === "custom" ? "12px" : "0" }}
        >
          <div className="filter-select-wrapper">
            <Select
              name="date_filter"
              label=""
              options={dateOptions}
              defaultValue={dateOptions.find(o => o.value === dateFilter)}
              handleChange={handleDateFilterChange}
              isSearchable={false}
              marignBottom
              width="180px"
            />
          </div>

          {dateFilter === "custom" && (
            <div className="custom-range-row d-flex align-items-center animate-fadeIn">
              <div className="input-group-custom d-flex align-items-center ">
                <span className="input-tag small font-weight-bold m-2 text-muted">
                  From
                </span>
                <DatePicker
                  selected={startDate ? new Date(startDate) : null}
                  onChange={date => handleCustomDateChange("start", date)}
                  dateFormat="dd-MMM-yyyy"
                  maxDate={new Date()}
                  className="date-picker-input-refined"
                  placeholderText="Start Date"
                />
              </div>
              <div className="input-group-custom d-flex align-items-center  ml-sm-3 mt-2 mt-sm-0">
                <span className="input-tag small font-weight-bold m-2 text-muted">
                  To
                </span>
                <DatePicker
                  selected={endDate ? new Date(endDate) : null}
                  onChange={date => handleCustomDateChange("end", date)}
                  dateFormat="dd-MMM-yyyy"
                  maxDate={new Date()}
                  className="date-picker-input-refined"
                  placeholderText="End Date"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomDateFilter;
